import React, { useState, useEffect, useRef } from 'react';
import { Box, Container, Typography, Button } from '@mui/joy';
import { animate } from 'motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';

// Import images
import dietPlanningImg from '../../assets/images/01_diet_planning.png';
import selectChefImg from '../../assets/images/02_select_chef.png';
import shoppingImg from '../../assets/images/03_shopping.png';
import plannedImg from '../../assets/images/04_planned.png';

// Import Footer
import Footer from '../layout/Footer';

const sections = [
  {
    id: 1,
    image: dietPlanningImg,
    title: "Personalized Diet Planning",
    subtitle: "AI-Powered Nutrition",
    description: "Transform your eating habits with our intelligent meal planning system. Get personalized nutrition recommendations based on your goals, preferences, and lifestyle.",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    textColor: "white"
  },
  {
    id: 2,
    image: selectChefImg,
    title: "Professional Chefs",
    subtitle: "Find a chef for meal prepping",
    description: "Work directly with certified nutritionists and professional chefs who understand your dietary needs and can create custom meal plans just for you on the go.",
    gradient: "linear-gradient(135deg, #7d02cf 0%, #667eea 100%)",
    textColor: "white"
  },
  {
    id: 3,
    image: shoppingImg,
    title: "Smart Shopping Assistant",
    subtitle: "Budget-smart Ingredient sourcing",
    description: "Never miss an ingredient again. Our smart shopping lists sync with your meal plans and help you find the best deals at local stores.",
    gradient: "linear-gradient(135deg, #764ba2 0%, #7d02cf 100%)",
    textColor: "white"
  },
  {
    id: 4,
    image: plannedImg,
    title: "Achieve Your Goals",
    subtitle: "Success Through Planning",
    description: "See real results with our comprehensive tracking system. Monitor your progress, celebrate milestones, and stay motivated on your wellness journey.",
    gradient: "linear-gradient(135deg, #667eea 0%, #7d02cf 100%)",
    textColor: "white"
  }
];

const AppleStyleScroll = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [showFooter, setShowFooter] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const scrollTop = window.pageYOffset;
      const containerTop = container.offsetTop;
      const containerHeight = container.offsetHeight;
      const windowHeight = window.innerHeight;

      // Calculate progress through the container
      const progress = Math.max(0, Math.min(1, 
        (scrollTop - containerTop + windowHeight) / (containerHeight + windowHeight)
      ));


      // Determine current section based on scroll progress
      // Reserve the last 20% of scroll for footer transition
      const sectionProgress = Math.min(progress * 1.25, 1);
      const sectionIndex = Math.floor(sectionProgress * sections.length);
      const clampedIndex = Math.max(0, Math.min(sections.length - 1, sectionIndex));
      
      if (clampedIndex !== currentSection) {
        setCurrentSection(clampedIndex);
      }

      // Show footer when we're past 80% scroll progress
      setShowFooter(progress > 0.8);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call

    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentSection]);

  useEffect(() => {
    // Animate content when section changes
    const textElements = containerRef.current?.querySelectorAll('.section-text');
    if (textElements) {
      animate(
        textElements,
        { 
          opacity: [0, 1],
          y: [30, 0]
        },
        { 
          duration: 0.8,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
        }
      );
    }
  }, [currentSection]);

  const currentSectionData = sections[currentSection];

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '500vh', // 4 sections + footer worth of scroll
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Fixed content container */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          background: showFooter ? 'transparent' : currentSectionData.gradient,
          transition: 'background 1s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 1
        }}
      >
        <Container
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            opacity: showFooter ? 0 : 1,
            transition: 'opacity 1s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {/* Text Content */}
          <Box
            sx={{
              width: { xs: '100%', md: '50%' },
              zIndex: 2,
              color: currentSectionData.textColor,
              pr: { xs: 0, md: 4 }
            }}
          >
            <Typography
              level="body1"
              className="section-text"
              sx={{
                mb: 1,
                opacity: 0.8,
                fontSize: { xs: 'sm', md: 'md' },
                fontWeight: 500,
                letterSpacing: '0.1em',
                textTransform: 'uppercase'
              }}
            >
              {currentSectionData.subtitle}
            </Typography>

            <Typography
              level="h1"
              className="section-text"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem', lg: '5rem' },
                fontWeight: 700,
                lineHeight: 1.1,
                mb: 3,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)'
              }}
            >
              {currentSectionData.title}
            </Typography>

            <Typography
              level="body1"
              className="section-text"
              sx={{
                fontSize: { xs: 'md', md: 'lg' },
                mb: 4,
                opacity: 0.9,
                maxWidth: '500px',
                lineHeight: 1.6
              }}
            >
              {currentSectionData.description}
            </Typography>

            {/* <Button
              component={Link}
              to="/services"
              size="lg"
              className="section-text"
              endDecorator={<ArrowForwardIcon />}
              sx={{
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: 'md',
                fontWeight: 600,
                '&:hover': {
                  background: 'rgba(255,255,255,0.25)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Learn More
            </Button> */}
          </Box>

          {/* Foreground Image */}
          <Box
            sx={{
              width: { xs: '100%', md: '50%' },
              height: { xs: '50vh', md: '75vh' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 3,
              position: { xs: 'relative', md: 'relative' },
              mt: { xs: 4, md: 0 }
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <img
                src={currentSectionData.image}
                alt={currentSectionData.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: 'scale(1)',
                }}
              />
            </Box>
          </Box>
        </Container>

        {/* Progress indicator */}
        <Box
          sx={{
            position: 'absolute',
            right: 40,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            zIndex: 3
          }}
        >
          {sections.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 3,
                height: 40,
                bgcolor: currentSection === index 
                  ? 'rgba(255,255,255,0.8)' 
                  : 'rgba(255,255,255,0.3)',
                borderRadius: 2,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />
          ))}
        </Box>

        {/* Scroll hint */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'rgba(255,255,255,0.7)',
            textAlign: 'center',
            animation: 'bounce 2s infinite',
            '@keyframes bounce': {
              '0%, 20%, 50%, 80%, 100%': {
                transform: 'translateX(-50%) translateY(0)'
              },
              '40%': {
                transform: 'translateX(-50%) translateY(-10px)'
              },
              '60%': {
                transform: 'translateX(-50%) translateY(-5px)'
              }
            }
          }}
        >
          <Typography level="body2" sx={{ mb: 1 }}>
            Scroll to explore
          </Typography>
          <Box sx={{ fontSize: '24px' }}>↓</Box>
        </Box>

        {/* Footer transition */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            opacity: showFooter ? 1 : 0,
            transform: showFooter ? 'translateY(0)' : 'translateY(50px)',
            transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: showFooter ? 'auto' : 'none'
          }}
        >
          <Footer />
        </Box>
      </Box>
    </Box>
  );
};

export default AppleStyleScroll;
